import {
  useMemo,
} from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  getApiSettingsGetToDoSettings,
  getApiToDoGetCommentsByToDoId,
  getApiUserGetAllUsersByTitleTitle,
  postApiToDoCommentOnToDo,
  postApiToDoCreateToDo,
  postApiToDoGetTodosByPatientId,
  postApiToDoRequestToDoList,
  putApiToDoUpdateToDo,
  putApiToDoUpdateToDoCollaborators,
} from 'endpoints';
import {
  CommentActivity,
  ToDo,
} from 'types/toDo';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  AsyncHandlerResult,
  ListQueryProps,
  ToDoSettings,
  ToDoUpdateHandlerProps,
  UpdateToDoRequestPayload,
} from 'pages/Dashboard/pages/Todos/types';
import {
  getDetailedToDo,
} from 'pages/Dashboard/pages/Todos/transport';
import {
  isNoAccessError,
} from 'utils/errors';
import {
  normalizeCommentActivity,
  normalizePanelToDos,
  normalizeToDos,
  normalizeValidToDoStatuses,
} from 'pages/Dashboard/pages/Todos/normalize';
import {
  PagePropertiesDTO,
  CreateToDoRequestDTO,
  SettingsDTO,
  ToDoCommentDTO,
  ToDoCommentRequestDTO,
  ToDoCommentsCollectionDTO,
  ToDoResponseDTO,
  ToDoUpdateRequestDTO,
  UpdateToDoCollaboratorsRequestDTO,
  UserDTO,
} from 'dtos';
import {
  normalizeFilterableSettings,
} from 'core/Filters/util';
import {
  useNotify,
} from 'core/hooks';
import {
  toDosEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type ToDoResponse = {
  data: ToDo[];
  pageProperties?: PagePropertiesDTO;
};

type PatientToDosQueryProps = {
  patientId?: number;
  page?: number;
  pageSize?: number;
  includeCompletedAndAbandoned?: boolean;
};

export const userToDoViewsQueryKey = ['user-to-do-views'] as const;

export function usePatientToDos(
  query: PatientToDosQueryProps,
): UseQueryResult<ToDoResponse, Error> {
  const {
    patientId,
    page,
    pageSize,
    includeCompletedAndAbandoned = false,
  } = query;

  return useQuery<ToDoResponse, Error>({
    queryKey: ['patient-to-dos', patientId, page, pageSize],
    queryFn: async () => {
      const response = await postApiToDoGetTodosByPatientId({
        patientId,
        includeCompletedAndAbandoned,
        pageRequest: {
          page: page ?? 0,
          pageSize: pageSize ?? 25,
        },
      });
      return {
        data: normalizePanelToDos(response.data ?? []),
        pageProperties: response.pageProperties,
      };
    },
    enabled: typeof patientId === 'number' && patientId > 0,
  });
}

export function useToDos(query: ListQueryProps): UseQueryResult<ToDoResponse, Error> {
  const { page, pageSize } = query;
  return useQuery<ToDoResponse, Error>({
    queryKey: ['to-dos', page, pageSize],
    queryFn: async () => {
      const response = await postApiToDoRequestToDoList({
        includeReferencedObjects: false,
        pageRequest: {
          page: page ?? 0,
          pageSize: pageSize ?? 25,
        },
      });
      return {
        data: normalizeToDos(response),
        pageProperties: response.toDos?.pageProperties,
      };
    },
  });
}

export function useDetailedToDo(toDoId: number): UseQueryResult<ToDo, Error> {
  return useQuery<ToDo, Error>({
    queryKey: [`to-do/${toDoId}`],
    queryFn: () => getDetailedToDo(toDoId),
    enabled: toDoId !== 0,
    throwOnError: isNoAccessError,
  });
}

export function useToDoComments(
  toDoId: number,
  enabled: boolean,
): UseQueryResult<CommentActivity[], Error> {
  return useQuery<CommentActivity[], Error>({
    queryKey: ['to-do-comments', toDoId],
    queryFn: async ({ signal }) => {
      const data = await getApiToDoGetCommentsByToDoId({
        toDoId,
        includeReferencedObjects: true,
      }, { signal });
      return normalizeCommentActivity(data as ToDoCommentsCollectionDTO);
    },
    enabled,
  });
}

export function useToDoMentionUsers(): UseQueryResult<UserDTO[], Error> {
  return usePrefetchedData<UserDTO[]>({
    key: ['toDoMentionUsers'],
    fetchFn: () => getApiUserGetAllUsersByTitleTitle(''),
  });
}

export function useToDoSettings(): ToDoSettings {
  const { data } = usePrefetchedData<SettingsDTO>({
    key: ['to-do-settings'],
    fetchFn: getApiSettingsGetToDoSettings,
  });
  const categories = data?.toDoCategorySettings ?? [];
  const statuses = data?.toDoStatusStartingStateSettings ?? [];
  const filterablePillSettings = data?.filterablePillSettings ?? [];
  return {
    categories: categories.map((item) => item.category as string),
    statuses: normalizeValidToDoStatuses(
      statuses.map((item) => item?.state ?? ''),
    ),
    filterablePillSettings,
  };
}

export function useCreateToDo(): UseMutationResult<
ToDoResponseDTO | void, Error, CreateToDoRequestDTO> {
  return useMutation<ToDoResponseDTO | void, Error, CreateToDoRequestDTO>({
    mutationFn: (data) => postApiToDoCreateToDo(data),
  });
}

export function useUpdateToDo():
  UseMutationResult<ToDoResponseDTO | void, Error, ToDoUpdateRequestDTO> {
  return useMutation<ToDoResponseDTO | void, Error, ToDoUpdateRequestDTO>({
    mutationFn: (data) => putApiToDoUpdateToDo(data),
  });
}

export function useCommentOnToDo():
  UseMutationResult<ToDoCommentDTO | void, Error, ToDoCommentRequestDTO> {
  return useMutation<ToDoCommentDTO | void, Error, ToDoCommentRequestDTO>({
    mutationFn: (data) => postApiToDoCommentOnToDo(data),
  });
}

export function useUpdateToDoHandler({
  successMessage = 'To-do updated successfully',
}: ToDoUpdateHandlerProps): AsyncHandlerResult {
  const notify = useNotify();
  const {
    mutateAsync,
    isPending,
  } = useUpdateToDo();

  const asyncHandler = async (payload: UpdateToDoRequestPayload) => {
    try {
      await mutateAsync(payload);
      toDosEventChannel.emit('onToDosChanged', payload.toDoId);
      notify({
        variant: 'success',
        message: successMessage,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return { isLoading: isPending, asyncHandler };
}

export function useUpdateToDoCollaborators({
  successMessage = 'Collaborators updated successfully!',
}: ToDoUpdateHandlerProps): AsyncHandlerResult {
  const notify = useNotify();
  const {
    mutateAsync,
    isPending,
  } = useMutation< ToDoResponseDTO | void, Error, UpdateToDoCollaboratorsRequestDTO>({
    mutationFn: (data) => putApiToDoUpdateToDoCollaborators(data),
  });

  const asyncHandler = async (payload: UpdateToDoCollaboratorsRequestDTO) => {
    try {
      await mutateAsync(payload);
      toDosEventChannel.emit('onToDosChanged', payload.toDoId);
    } catch (e) {
      console.error(e);
      return;
    }

    notify({
      variant: 'success',
      message: successMessage,
    });
  };

  return { isLoading: isPending, asyncHandler };
}

export function useToDosFilterMetadata() {
  const { data } = usePrefetchedData<SettingsDTO>({
    key: ['to-do-settings'],
    fetchFn: getApiSettingsGetToDoSettings,
  });

  return useMemo(
    () => normalizeFilterableSettings(data?.filterableSettings ?? []),
    [data?.filterableSettings],
  );
}
